import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

/**
 * Lazily loads the product-page components that the quick-add dialog renders.
 * Quick-add fetches the full product page and injects it, so these "buy area"
 * components can appear inside the dialog on any page with quick-add. They are
 * loaded eagerly on every template except collection & search; on those two they
 * are loaded here on first quick-add use so they don't weigh down the initial
 * page load. Memoized; resets on failure so a later interaction can retry.
 * @type {Promise<unknown> | null}
 */
let quickAddModulesPromise = null;
function ensureQuickAddModules() {
  if (quickAddModulesPromise) return quickAddModulesPromise;
  quickAddModulesPromise = Promise.all([
    import('@theme/product-form'),
    import('@theme/media-gallery'),
    import('@theme/component-quantity-selector'),
    import('@theme/product-inventory'),
    import('@theme/volume-pricing'),
    import('@theme/volume-pricing-info'),
    import('@theme/price-per-item'),
    import('@theme/back-in-stock'),
  ]).catch((error) => {
    // Reset so the next interaction can retry; the dialog still opens (the
    // collection page already loads variant-picker/product-price/etc.).
    quickAddModulesPromise = null;
    console.error('[quick-add] Failed to load quick-add dialog modules', error);
  });
  return quickAddModulesPromise;
}

/**
 * Section id of the product template's main "product-information" section. Quick-add uses it to
 * fetch ONLY the buy area via the Section Rendering API (?section_id=…) instead of the whole
 * product page (~940KB vs ~175KB), which is what made the "choices" block slow to appear.
 *
 * Every product on the same template shares this id, but the numeric part is theme-specific
 * (it differs between the live theme and any preview/duplicate). So this is only a *seed*: it
 * lets the first quick-add on the live theme already be fast. If it turns out wrong on another
 * theme, fetchProductPage falls back to a full product-page fetch and re-learns the correct id
 * from that response, self-correcting for whatever theme is running. Nothing breaks either way.
 * @type {string}
 */
let productMainSectionId = 'template--28834665070979__main';

/**
 * The quick-add dialog is a single shared element on the page, so several product cards can each
 * kick off an async open. This counter marks the most recent open; a card whose fetch resolves
 * after another card has taken over (its token no longer matches) skips injecting its content, so
 * the dialog always shows the product the shopper tapped last.
 * @type {number}
 */
let activeQuickAddToken = 0;

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** In-flight grid fetches keyed by url, so a prefetch and the subsequent click share one request.
   * @type {Map<string, Promise<Element | null>>} */
  #gridPromises = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));

    // Prefetch the product section as soon as the shopper hovers (desktop) or presses down
    // (touch) on the card, so the dialog content is usually already cached by the time the
    // click fires. Best-effort and idempotent — safe to fire repeatedly.
    this.addEventListener('pointerenter', this.#prefetch);
    this.addEventListener('pointerdown', this.#prefetch);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Returns the product grid element for a url, from cache when available, otherwise fetching and
   * caching it. Concurrent callers (a hover prefetch and the following click) share one request.
   * @param {string} url
   * @returns {Promise<Element | null>}
   */
  #getProductGrid(url) {
    const cached = this.#cachedContent.get(url);
    if (cached) return Promise.resolve(cached);

    const pending = this.#gridPromises.get(url);
    if (pending) return pending;

    const request = this.fetchProductPage(url)
      .then((html) => {
        const gridElement = html?.querySelector('[data-product-grid-content]');
        if (!gridElement) return null;
        // Cache the cloned element to avoid modifying the original.
        const grid = /** @type {Element} */ (gridElement.cloneNode(true));
        this.#cachedContent.set(url, grid);
        return grid;
      })
      .catch(() => null)
      .finally(() => this.#gridPromises.delete(url));

    this.#gridPromises.set(url, request);
    return request;
  }

  /**
   * Best-effort warm-up of the dialog content on hover/press, before the click fires.
   */
  #prefetch = () => {
    // Only the "choose" flow opens the dialog; the direct "add" flow never needs the section.
    if (this.dataset.quickAddButton !== 'choose') return;
    const url = this.productPageUrl;
    if (!url || this.#cachedContent.has(url) || this.#gridPromises.has(url)) return;
    this.#getProductGrid(url);
  };

  /**
   * Renders a lightweight placeholder in the modal so it can open instantly while content loads.
   */
  #showSkeleton() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    // Heights roughly match the loaded content (title, price, one row of size buttons, add-to-cart)
    // so the bottom sheet doesn't noticeably resize when the real content replaces the skeleton.
    const chips = '<div class="quick-add-modal__skeleton-chip"></div>'.repeat(6);
    modalContent.innerHTML = `
      <div class="quick-add-modal__skeleton" aria-hidden="true">
        <div class="quick-add-modal__skeleton-bar" style="height: 26px; width: 66%;"></div>
        <div class="quick-add-modal__skeleton-bar" style="height: 20px; width: 32%;"></div>
        <div class="quick-add-modal__skeleton-row">${chips}</div>
        <div class="quick-add-modal__skeleton-bar" style="height: 50px; width: 100%; margin-top: 4px; border-radius: 8px;"></div>
      </div>`;
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    // Make sure the dialog's product components are defined before we inject the fetched markup.
    // Kicked off here in parallel with the fetch below; on most templates it's an instant no-op.
    const modulesReady = ensureQuickAddModules();

    const currentUrl = this.productPageUrl;

    // Claim the shared dialog for this interaction; a later click bumps the token and any in-flight
    // fetch from an earlier click will notice the mismatch and skip injecting stale content.
    const token = ++activeQuickAddToken;

    // Fast path: content already available (from a hover/press prefetch or a previous open). Fill
    // first, then open, so there's no flash of the previously shown product.
    const cachedGrid = this.#cachedContent.get(currentUrl);
    if (cachedGrid) {
      await modulesReady;
      if (token !== activeQuickAddToken) return;
      await this.updateQuickAddModal(/** @type {Element} */ (cachedGrid.cloneNode(true)));
      this.#updateVariantPicker(cachedGrid);
      this.#openQuickAddModal();
      return;
    }

    // Otherwise open the dialog immediately with a skeleton so the tap feels instant, then fetch
    // the content and fill it in.
    this.#showSkeleton();
    this.#openQuickAddModal();

    const productGrid = await this.#getProductGrid(currentUrl);

    // Ensure the lazily-loaded dialog components are defined before injecting markup.
    await modulesReady;

    // Abort if another quick-add has since taken over the shared dialog.
    if (token !== activeQuickAddToken) return;

    if (productGrid) {
      // Use a fresh clone from the cache.
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    } else {
      // Both the section and full-page fetches failed — don't strand the shopper on a skeleton.
      this.#closeQuickAddModal();
    }
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      // Perf: fetch only the product's main section (buy area) via the Section Rendering API
      // instead of the full product page. Cuts the payload from ~940KB to ~175KB and skips
      // rendering the header, footer, reviews, related sections and ~120 extra <script> tags,
      // which is what made the quick-add "choices" block slow to appear on mobile.
      if (productMainSectionId) {
        const sectionUrl = new URL(productPageUrl);
        sectionUrl.searchParams.set('section_id', productMainSectionId);

        const sectionResponse = await fetch(sectionUrl.toString(), {
          signal: this.#abortController.signal,
        });

        if (sectionResponse.ok) {
          const sectionHtml = new DOMParser().parseFromString(await sectionResponse.text(), 'text/html');
          // Only trust the section render if it actually contains the buy area. If the seeded id
          // belongs to a different theme/template, we get a 404 (not ok) or an empty shell, and
          // fall through to the full-page fetch below, which also re-learns the correct id.
          if (sectionHtml.querySelector('[data-product-grid-content] product-form-component')) {
            return sectionHtml;
          }
        }
      }

      // Full product-page fetch: used the first time, and whenever the section fetch above did
      // not yield the buy area. Re-learn the current theme's product section id from the response
      // so subsequent quick-adds can use the lightweight section fetch.
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      const mainSection = html.querySelector('[id^="shopify-section-template--"][id$="__main"]');
      if (mainSection) {
        productMainSectionId = mainSection.id.replace('shopify-section-', '');
      }

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
